import React, { useState, useRef, useEffect } from 'react';
import { Session } from '../../agent/session';
import { Timer } from '../timer/Timer';
import { CONFIG } from '../../config';

import './InputPanel.scss';

import { ReactComponent as SubmitIcon } from '../../assets/icons/submit-icon.svg';
import { ReactComponent as MutedIcon } from '../../assets/icons/muted-icon.svg';
import { ReactComponent as UnMutedIcon } from '../../assets/icons/unmuted-icon.svg';

import { Mic, Phone, PhoneOff } from 'react-feather';


interface InputPanelProps {
  sessionRef: React.MutableRefObject<Session>;
  onSubmit: () => void;
  bottomBar?: boolean;
}

export function InputPanel({
  sessionRef,
  onSubmit,
  bottomBar = false,
  ...rest
}: InputPanelProps) {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(sessionRef.current.getMuted());
  const [canPushToTalk, setCanPushToTalk] = useState(true);
  const [conversationStartTime] = useState<number>(Date.now());
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /**
   * In push-to-talk mode, start recording
   * .appendInputAudio() for each sample
   */
  const startRecording = async () => {
    setIsRecording(true);
    await sessionRef.current.initAudioplayer().then(async () => {
      await sessionRef.current.startRecording();
    });
  };

  /**
   * In push-to-talk mode, stop recording
   */
  const stopRecording = async () => {
    setIsRecording(false);
    onSubmit();
    await sessionRef.current.stopRecording();
  };

  const sendText = (text: string) => {
    if (text.trim()) {
      sessionRef.current.sendUserText(text);
      onSubmit();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sessionRef.current.initAudioplayer().then(() => {
      const currentText = message;
      // First clear the message state
      setMessage('');
      // Then send the text
      sendText(currentText);
      // Finally adjust the height
      if (textareaRef.current) {
        // Force a reset of the textarea's height
        textareaRef.current.style.height = bottomBar ? '24px' : '110px';
        textareaRef.current.style.overflowY = 'hidden';
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Allow new line with Shift+Enter
        return;
      }
      // Submit with Enter
      e.preventDefault();
      sessionRef.current.initAudioplayer().then(() => {
        handleSubmit(e);
      });
    }
  };

  const adjustHeight = (element: HTMLTextAreaElement) => {
    // Store the current scroll position
    const scrollPos = window.scrollY;

    // Reset to minimum height to properly calculate new height
    element.style.height = 'auto';

    // Calculate max height (30% of viewport height)
    const maxHeight = window.innerHeight * 0.3;

    // If content height exceeds max height, set to max height and allow scrolling
    if (element.scrollHeight > maxHeight) {
      element.style.height = `${maxHeight}px`;
      element.style.overflowY = 'auto';
    } else {
      // Otherwise, set to content height and disable scrolling
      element.style.height = `${element.scrollHeight}px`;
      element.style.overflowY = 'hidden';
    }

    // Restore scroll position to prevent page jump
    window.scrollTo(0, scrollPos);
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    if (textareaRef.current) {
      adjustHeight(textareaRef.current);
    }
  };

  const toggleMute = () => {
    sessionRef.current.setMuted(!isMuted);
    setIsMuted(!isMuted);
  }

  useEffect(() => {
    if (textareaRef.current) {
      // Set initial styles
      adjustHeight(textareaRef.current);
    }
  }, []);

  /**
   * Switch between Manual <> VAD mode for communication
   */
  const changeTurnEndType = async (value: string) => {
    await sessionRef.current.changeTurnEndType(value, () => {
      setCanPushToTalk(value === 'none');
    });
  };

  return (
    <div autoFocus data-component="InputPanel" {...rest}>
      <div>
        <div className="panel-input-container">
          {canPushToTalk ?
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              placeholder="Message Trump"
              className={`panel-message-input${bottomBar ? " panel-message-input-bottombar" : ""}`}
              rows={1}
            /> :
            <textarea
              placeholder="End phone call to send text"
              disabled={true}
              className={`panel-message-input${bottomBar ? " panel-message-input-bottombar" : ""}`}
            />
          }
        </div>
        <div className={`input-bar input-bar-textarea-visible`}>
          <button
            className={`panel-action-button ${isMuted && "panel-action-button-turnon"}`}
            onClick={() => toggleMute()}
          >
            {isMuted ? <MutedIcon /> : <UnMutedIcon />}
          </button>
          <div className="panel-input-icons">
            <Timer 
              startTime={conversationStartTime}
              duration={CONFIG.CONVERSATION_TIMEOUT_MS}
              onTimeout={() => {
                sessionRef.current.disconnect();
              }}
            />
            {canPushToTalk &&
              <button
                className={`panel-action-button ${isRecording && "panel-action-button-recording"}`}
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
                style={isRecording ? { background: 'grey' } : {}}
              >
                <Mic />
              </button>
            }
            {!message.trim() ?
              (canPushToTalk ?
                <button onClick={() => {
                  // Initialize the audio player after a user gesture on the page.
                  sessionRef.current.initAudioplayer().then(() => {
                    changeTurnEndType('server_vad');
                  });
                }} className="panel-submit-button" style={{ background: 'darkred' }}>
                  <Phone style={{ color: 'white' }} />
                </button> :
                <button onClick={() => changeTurnEndType('none')} className="panel-submit-button" style={{ background: 'red' }}>
                  <PhoneOff style={{ color: 'white' }} />
                </button>
              ) :
              <button onClick={(e) => { handleSubmit(e) }} className="panel-submit-button" disabled={!message.trim()}>
                <SubmitIcon className="panel-icon-2xl" />
              </button>
            }
          </div>
        </div>
      </div>
    </div>
  );
}
